import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiCall } from '../helper/axios';
// Initial state, we can hydrate this from localStorage if needed, or get it from API response
const getInitialState = () => {
    let savedState = null;
    try {
        const saved = localStorage.getItem('presentation_mode_state');
        if (saved) {
            savedState = JSON.parse(saved);
        }
    }
    catch (error) {
        console.error("Error reading presentation state from local storage:", error);
    }
    return {
        isPresentationMode: savedState?.isPresentationMode || false,
        presentationClients: savedState?.presentationClients || [],
        presentationRoleId: savedState?.presentationRoleId || null,
        globalClients: [],
        loading: false,
        error: null,
    };
};
const initialState = getInitialState();
// Thunks
export const fetchGlobalClientsAsync = createAsyncThunk('presentation/fetchGlobalClients', async (token, { rejectWithValue }) => {
    try {
        const res = await ApiCall("GET", "/api/utility/clients/active", null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return res.data.data.clients;
        }
        return rejectWithValue("Failed to fetch clients");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Failed to fetch clients");
    }
});
export const enablePresentationModeAsync = createAsyncThunk('presentation/enablePresentationMode', async (payload, { rejectWithValue }) => {
    try {
        const { token, ...data } = payload;
        const res = await ApiCall("PUT", "/api/auth/presentation-mode", data, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return data; // Return the applied presentation mode data
        }
        return rejectWithValue(res?.data?.message || "Failed to enable Presentation Mode");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Failed to enable Presentation Mode");
    }
});
export const presentationSlice = createSlice({
    name: 'presentation',
    initialState,
    reducers: {
        setPresentationMode: (state, action) => {
            state.isPresentationMode = action.payload.isPresentationMode;
            state.presentationClients = action.payload.presentationClients;
            state.presentationRoleId = action.payload.presentationRoleId;
            // Save to localStorage so it persists on reload
            try {
                localStorage.setItem('presentation_mode_state', JSON.stringify({
                    isPresentationMode: state.isPresentationMode,
                    presentationClients: state.presentationClients,
                    presentationRoleId: state.presentationRoleId,
                }));
            }
            catch (error) {
                console.error("Error saving presentation state to local storage:", error);
            }
        },
        clearPresentationMode: (state) => {
            state.isPresentationMode = false;
            state.presentationClients = [];
            state.presentationRoleId = null;
            try {
                localStorage.removeItem('presentation_mode_state');
            }
            catch (error) {
                console.error("Error removing presentation state from local storage:", error);
            }
        },
    },
    extraReducers: (builder) => {
        // fetchGlobalClientsAsync
        builder.addCase(fetchGlobalClientsAsync.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchGlobalClientsAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.globalClients = action.payload;
        });
        builder.addCase(fetchGlobalClientsAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // enablePresentationModeAsync
        builder.addCase(enablePresentationModeAsync.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(enablePresentationModeAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.isPresentationMode = action.payload.presentationMode;
            state.presentationClients = action.payload.presentationClients;
            state.presentationRoleId = null;
            try {
                localStorage.setItem('presentation_mode_state', JSON.stringify({
                    isPresentationMode: state.isPresentationMode,
                    presentationClients: state.presentationClients,
                    presentationRoleId: state.presentationRoleId,
                }));
            }
            catch (error) {
                console.error("Error saving presentation state to local storage:", error);
            }
        });
        builder.addCase(enablePresentationModeAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});
export const { setPresentationMode, clearPresentationMode } = presentationSlice.actions;
export default presentationSlice.reducer;
